import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import type { InputApiCallConfig } from './dvm-layout-builder.types';
import { buildApiCallBody, buildApiCallParams, mergeComputeResponses, targetsExcluding } from './dvm-input-api-call.utils';

/**
 * Runs the recompute request an input control triggers: the normal compute
 * API, or — when the control has a custom API call configured — that API
 * instead of (mode `replace`) or alongside (mode `parallel`) the compute
 * API, with the two responses merged.
 */
@Injectable({ providedIn: 'root' })
export class DvmInputComputeService {
  private readonly http = inject(HttpClient);

  resolve(
    apiConfig: InputApiCallConfig | undefined,
    fallbackUrl: string,
    fallbackPayload: Record<string, unknown>,
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    if (!apiConfig?.enabled || !apiConfig.url?.trim()) {
      return this.http.post<Record<string, unknown>>(fallbackUrl, fallbackPayload);
    }
    const custom$ = this.customApiCall$(apiConfig, tokens);
    if (apiConfig.mode !== 'parallel') return custom$;

    const customIds = new Set((apiConfig.targets ?? []).map(String).filter(Boolean));
    const computeTargets = targetsExcluding(fallbackPayload, customIds);
    if (!computeTargets.length) return custom$;
    const compute$ = this.http.post<Record<string, unknown>>(fallbackUrl, { ...fallbackPayload, targets: computeTargets });
    return forkJoin([custom$, compute$]).pipe(
      map(([customResponse, computeResponse]) => mergeComputeResponses(customResponse, computeResponse, customIds)),
    );
  }

  private customApiCall$(
    apiConfig: { url?: string; method?: string; bodyTemplate?: string },
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    const method = (apiConfig.method || 'POST').toUpperCase();
    if (method === 'GET') {
      return this.http.get<Record<string, unknown>>(apiConfig.url!, { params: buildApiCallParams(tokens) });
    }
    const body = buildApiCallBody(apiConfig.bodyTemplate, tokens);
    if (method === 'PUT') return this.http.put<Record<string, unknown>>(apiConfig.url!, body);
    if (method === 'PATCH') return this.http.patch<Record<string, unknown>>(apiConfig.url!, body);
    return this.http.post<Record<string, unknown>>(apiConfig.url!, body);
  }
}
